import { DeliveryStopPod } from "api/response";
import { formatDate } from "helpers/dateTimeHelper";

const MENU_TIMESTAMP_FORMAT = "D MMMM, HH:mm";
const SELECTED_TIMESTAMP_FORMAT = "D MMM, HH:mm";
const AIRPORT_CODE_PATTERN = /,?\s*Airport code:\s*([A-Z0-9]{3,4})\s*$/i;
const SEAPORT_CODE_PATTERN = /,?\s*Seaport code:\s*([A-Z0-9]+)\s*$/i;

export function extractPortCodeFromAddress(fullAddress: string): string | null {
  const airportMatch = fullAddress.match(AIRPORT_CODE_PATTERN);
  if (airportMatch?.[1]) {
    return airportMatch[1].toUpperCase();
  }

  const seaportMatch = fullAddress.match(SEAPORT_CODE_PATTERN);
  if (seaportMatch?.[1]) {
    return seaportMatch[1].toUpperCase();
  }

  return null;
}

export function stripPortCodeFromAddress(fullAddress: string): string {
  return fullAddress
    .replace(AIRPORT_CODE_PATTERN, "")
    .replace(SEAPORT_CODE_PATTERN, "")
    .replace(/,\s*$/, "")
    .trim();
}

function formatProviderTimestamp(stop: DeliveryStopPod, format: string): string {
  if (stop.provider_date && stop.provider_time) {
    return formatDate(`${stop.provider_date} ${stop.provider_time}`, format);
  }
  if (stop.provider_timestamp) {
    return formatDate(stop.provider_timestamp, format);
  }
  return "-";
}

export function formatDeliveryStopProviderTimestamp(stop: DeliveryStopPod): string {
  return formatProviderTimestamp(stop, MENU_TIMESTAMP_FORMAT);
}

export function formatDeliveryStopMenuTimestamp(stop: DeliveryStopPod): string {
  return formatProviderTimestamp(stop, MENU_TIMESTAMP_FORMAT);
}

export function formatDeliveryStopSelectedTimestamp(stop: DeliveryStopPod): string {
  return formatProviderTimestamp(stop, SELECTED_TIMESTAMP_FORMAT);
}

export function formatDeliveryStopLocationTitle(stop: DeliveryStopPod): string {
  if (stop.location_title) {
    return `Stop ${stop.stop_number}: ${stop.location_title}`;
  }
  return `Stop ${stop.stop_number}`;
}

function normalizeForComparison(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Returns the address name to render above the full address, or `null` when it
 * would only repeat the row title or the start of the full address.
 */
export function getDeliveryStopAddressName(stop: DeliveryStopPod): string | null {
  const name = stop.location_name?.trim();
  if (!name) {
    return null;
  }

  const normalizedName = normalizeForComparison(name);
  const normalizedTitle = normalizeForComparison(stop.location_title ?? "");
  const normalizedAddress = normalizeForComparison(stop.full_address ?? "");

  if (normalizedName === normalizedTitle || normalizedName === normalizedAddress) {
    return null;
  }

  if (normalizedAddress.startsWith(`${normalizedName},`)) {
    return null;
  }

  return name;
}

/** Address, airport, or seaport label shown on the second line of menu options. */
export function getDeliveryStopLocationLine(stop: DeliveryStopPod): string | null {
  const name = stop.location_name?.trim();
  if (name) {
    return name;
  }

  const title = stop.location_title?.trim();
  return title || null;
}

/** Location line with airport/seaport code suffix, e.g. "Singapore Changi Airport (SIN)". */
export function getDeliveryStopLocationLineWithCode(stop: DeliveryStopPod): string | null {
  const locationLine = getDeliveryStopLocationLine(stop);
  if (!locationLine) {
    return null;
  }

  const portCode = stop.full_address ? extractPortCodeFromAddress(stop.full_address) : null;
  if (!portCode) {
    return locationLine;
  }

  return `${locationLine} (${portCode})`;
}

/** Street address shown on the third menu line when it adds information beyond the location line. */
export function getDeliveryStopStreetAddress(stop: DeliveryStopPod): string | null {
  const address = stop.full_address?.trim();
  if (!address) {
    return null;
  }

  const locationLine = getDeliveryStopLocationLine(stop);
  let streetAddress = address;

  if (locationLine) {
    const normalizedAddress = normalizeForComparison(address);
    const normalizedLocation = normalizeForComparison(locationLine);

    if (normalizedAddress === normalizedLocation) {
      streetAddress = "";
    } else if (normalizedAddress.startsWith(`${normalizedLocation},`)) {
      streetAddress = address.slice(locationLine.length + 1).trim();
    }
  }

  streetAddress = stripPortCodeFromAddress(streetAddress);

  return streetAddress || null;
}

export function formatDeliveryStopSelectedLabel(stop: DeliveryStopPod): string {
  const locationLabel = getDeliveryStopLocationLineWithCode(stop) ?? `Stop ${stop.stop_number}`;
  const timestamp = formatDeliveryStopSelectedTimestamp(stop);
  return `Stop ${stop.stop_number}: ${locationLabel} - ${timestamp}`;
}

/** Legacy compact label used by multistop POD and other existing selects. */
export function formatDeliveryStopOptionLabel(stop: DeliveryStopPod): string {
  const locationLabel = stop.location_name || stop.location_title || `Stop ${stop.stop_number}`;
  const timestamp = formatDeliveryStopProviderTimestamp(stop);
  return `Stop ${stop.stop_number}: ${locationLabel} - ${timestamp}`;
}

export function isStopPodDelivered(stop: DeliveryStopPod): boolean {
  return Boolean(stop.delivered_at);
}
